/**
 * Capacitor Native Utilities
 * Provides native functionality when running as a mobile app
 */

import { Capacitor } from '@capacitor/core';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { App } from '@capacitor/app';
import { Keyboard } from '@capacitor/keyboard';

// Check if running as native app
export const isNative = () => Capacitor.isNativePlatform();
export const getPlatform = () => Capacitor.getPlatform();
export const isIOS = () => Capacitor.getPlatform() === 'ios';
export const isAndroid = () => Capacitor.getPlatform() === 'android';

/**
 * Initialize native features
 */
export const initializeNative = async () => {
  if (!isNative()) return;

  try {
    // Hide splash screen after app loads
    await SplashScreen.hide();

    // Set status bar style
    await StatusBar.setStyle({ style: Style.Light });
    
    if (isAndroid()) {
      await StatusBar.setBackgroundColor({ color: '#5DBDB3' });
    }

    // Listen for app state changes
    App.addListener('appStateChange', ({ isActive }) => {
      console.log('App state changed. Is active:', isActive);
    });

    // Listen for back button on Android
    App.addListener('backButton', ({ canGoBack }) => {
      if (canGoBack) {
        window.history.back();
      } else {
        App.exitApp();
      }
    });

    // Keyboard listeners
    Keyboard.addListener('keyboardWillShow', (info) => {
      console.log('Keyboard will show with height:', info.keyboardHeight);
    });

    Keyboard.addListener('keyboardWillHide', () => {
      console.log('Keyboard will hide');
    });

  } catch (error) {
    console.error('Error initializing native features:', error);
  }
};

/**
 * Haptic feedback
 */
export const hapticFeedback = {
  light: async () => {
    if (!isNative()) return;
    try {
      await Haptics.impact({ style: ImpactStyle.Light });
    } catch (e) {
      console.log('Haptics not available');
    }
  },
  medium: async () => {
    if (!isNative()) return;
    try {
      await Haptics.impact({ style: ImpactStyle.Medium });
    } catch (e) {
      console.log('Haptics not available');
    }
  },
  heavy: async () => {
    if (!isNative()) return;
    try {
      await Haptics.impact({ style: ImpactStyle.Heavy });
    } catch (e) {
      console.log('Haptics not available');
    }
  },
  success: async () => {
    if (!isNative()) return;
    try {
      await Haptics.notification({ type: NotificationType.Success });
    } catch (e) {
      console.log('Haptics not available');
    }
  },
  warning: async () => {
    if (!isNative()) return;
    try {
      await Haptics.notification({ type: NotificationType.Warning });
    } catch (e) {
      console.log('Haptics not available');
    }
  },
  error: async () => {
    if (!isNative()) return;
    try {
      await Haptics.notification({ type: NotificationType.Error });
    } catch (e) {
      console.log('Haptics not available');
    }
  }
};

/**
 * Take a photo using native camera
 * Falls back to file input on web
 */
export const takePhoto = async (options = {}) => {
  const defaultOptions = {
    quality: 90,
    allowEditing: false,
    resultType: CameraResultType.Base64,
    source: CameraSource.Camera,
    saveToGallery: false,
    correctOrientation: true,
    presentationStyle: 'fullscreen',
    ...options
  };

  try {
    if (isNative()) {
      const image = await Camera.getPhoto(defaultOptions);
      await hapticFeedback.light();
      return {
        base64: image.base64String,
        dataUrl: `data:image/${image.format};base64,${image.base64String}`,
        format: image.format,
        path: image.path
      };
    } else {
      // Web fallback - return null to use file input
      return null;
    }
  } catch (error) {
    if (error.message?.includes('User cancelled')) {
      return null;
    }
    throw error;
  }
};

/**
 * Pick a photo from gallery
 */
export const pickPhoto = async (options = {}) => {
  const defaultOptions = {
    quality: 90,
    allowEditing: false,
    resultType: CameraResultType.Base64,
    source: CameraSource.Photos,
    ...options
  };

  try {
    if (isNative()) {
      const image = await Camera.getPhoto(defaultOptions);
      await hapticFeedback.light();
      return {
        base64: image.base64String,
        dataUrl: `data:image/${image.format};base64,${image.base64String}`,
        format: image.format,
        path: image.path
      };
    } else {
      return null;
    }
  } catch (error) {
    if (error.message?.includes('User cancelled')) {
      return null;
    }
    throw error;
  }
};

/**
 * Show/hide status bar
 */
export const statusBar = {
  show: async () => {
    if (!isNative()) return;
    try {
      await StatusBar.show();
    } catch (e) {
      console.log('StatusBar not available');
    }
  },
  hide: async () => {
    if (!isNative()) return;
    try {
      await StatusBar.hide();
    } catch (e) {
      console.log('StatusBar not available');
    }
  },
  setLight: async () => {
    if (!isNative()) return;
    try {
      await StatusBar.setStyle({ style: Style.Light });
    } catch (e) {
      console.log('StatusBar not available');
    }
  },
  setDark: async () => {
    if (!isNative()) return;
    try {
      await StatusBar.setStyle({ style: Style.Dark });
    } catch (e) {
      console.log('StatusBar not available');
    }
  }
};

/**
 * Keyboard utilities
 */
export const keyboard = {
  show: async () => {
    if (!isNative()) return;
    try {
      await Keyboard.show();
    } catch (e) {
      console.log('Keyboard not available');
    }
  },
  hide: async () => {
    if (!isNative()) return;
    try {
      await Keyboard.hide();
    } catch (e) {
      console.log('Keyboard not available');
    }
  }
};

/**
 * App utilities
 */
export const app = {
  exitApp: async () => {
    if (!isNative()) return;
    await App.exitApp();
  },
  getInfo: async () => {
    if (!isNative()) return null;
    return await App.getInfo();
  },
  getLaunchUrl: async () => {
    if (!isNative()) return null;
    return await App.getLaunchUrl();
  }
};

export default {
  isNative,
  getPlatform,
  isIOS,
  isAndroid,
  initializeNative,
  hapticFeedback,
  takePhoto,
  pickPhoto,
  statusBar,
  keyboard,
  app
};
